import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

/* Espelha `TipoDocumentoEnum` e `StatusDocumentoEnum` da API
   (Brik.Domain/Enum). Objeto `as const` em vez de `enum` por causa do
   `erasableSyntaxOnly` do tsconfig. */
export const TipoDocumento = { Proposta: 1, Orcamento: 2, Contrato: 3 } as const;
export type TipoDocumento = typeof TipoDocumento[keyof typeof TipoDocumento];

export const StatusDocumento = { Emitido: 1, Aceito: 2, Recusado: 3, Cancelado: 4 } as const;
export type StatusDocumento = typeof StatusDocumento[keyof typeof StatusDocumento];

export const NOME_TIPO_DOCUMENTO: Record<TipoDocumento, string> = {
    [TipoDocumento.Proposta]: 'Proposta',
    [TipoDocumento.Orcamento]: 'Orçamento',
    [TipoDocumento.Contrato]: 'Contrato',
};

export const NOME_STATUS_DOCUMENTO: Record<StatusDocumento, string> = {
    [StatusDocumento.Emitido]: 'Emitido',
    [StatusDocumento.Aceito]: 'Aceito',
    [StatusDocumento.Recusado]: 'Recusado',
    [StatusDocumento.Cancelado]: 'Cancelado',
};

export const OPCOES_TIPO_DOCUMENTO = (Object.values(TipoDocumento) as TipoDocumento[])
    .map(v => ({ value: String(v), label: NOME_TIPO_DOCUMENTO[v] }));

export interface ModeloDocumento {
    modeloDocumentoId: number;
    nome: string;
    tipo: TipoDocumento;
    /** JSON do editor (Tiptap), serializado como string. */
    conteudoJson: string;
    validadeDias: number | null;
    ativo: boolean;
    dataCriacao: string;
    dataAtualizacao: string;
}

export interface VariavelDocumento {
    chave: string;
    nome: string;
    grupo: string;
    exemplo: string;
}

export interface CriarModeloDocumentoDTO {
    nome: string;
    tipo: TipoDocumento;
    conteudoJson: string;
    validadeDias: number | null;
    ativo: boolean;
}

export interface AtualizarModeloDocumentoDTO extends CriarModeloDocumentoDTO {
    modeloDocumentoId: number;
}

interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

const json = { 'Content-Type': 'application/json' };

const lerOuFalhar = async <T,>(res: Response, padrao: string): Promise<ApiResponse<T>> => {
    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.sucesso) throw new Error(data?.mensagem || padrao);
    return data;
};

export const ModeloDocumentoService = {
    /** Modelos da empresa. `somenteAtivos` é o que a emissão usa. */
    buscarTodos: async (tipo?: TipoDocumento, somenteAtivos = false): Promise<ApiResponse<ModeloDocumento[]>> => {
        const q = new URLSearchParams();
        if (tipo) q.set('tipo', String(tipo));
        if (somenteAtivos) q.set('somenteAtivos', 'true');
        const res = await apiFetch(`${API_URL}/api/ModeloDocumento/BuscarTodos?${q.toString()}`);
        if (!res.ok) {
            if (res.status === 400 || res.status === 404) return { sucesso: true, mensagem: '', dados: [] };
            throw new Error('Erro ao buscar modelos de documento');
        }
        return res.json();
    },

    buscarPorId: async (id: number): Promise<ApiResponse<ModeloDocumento>> => {
        const res = await apiFetch(`${API_URL}/api/ModeloDocumento/BuscarPorId/${id}`);
        return lerOuFalhar(res, 'Modelo não encontrado.');
    },

    /** Catálogo fixo + um `campo.<chave>` por campo personalizado da empresa. */
    variaveis: async (): Promise<ApiResponse<VariavelDocumento[]>> => {
        const res = await apiFetch(`${API_URL}/api/ModeloDocumento/Variaveis`);
        return lerOuFalhar(res, 'Não foi possível carregar as variáveis.');
    },

    criar: async (dto: CriarModeloDocumentoDTO): Promise<ApiResponse<ModeloDocumento>> => {
        const res = await apiFetch(`${API_URL}/api/ModeloDocumento/Criar`, {
            method: 'POST', headers: json, body: JSON.stringify(dto),
        });
        return lerOuFalhar(res, 'Não foi possível criar o modelo.');
    },

    atualizar: async (dto: AtualizarModeloDocumentoDTO): Promise<ApiResponse<ModeloDocumento>> => {
        const res = await apiFetch(`${API_URL}/api/ModeloDocumento/Atualizar`, {
            method: 'PUT', headers: json, body: JSON.stringify(dto),
        });
        return lerOuFalhar(res, 'Não foi possível salvar o modelo.');
    },

    remover: async (id: number): Promise<ApiResponse<null>> => {
        const res = await apiFetch(`${API_URL}/api/ModeloDocumento/Remover/${id}`, { method: 'DELETE' });
        return lerOuFalhar(res, 'Não foi possível excluir o modelo.');
    },
};

export default ModeloDocumentoService;
